import { FC } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { MarginProps, padding, PaddingProps } from '@mui/system';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import { getToolbarUtilityClass } from './paneClasses';
import { clsx } from 'clsx';
import capitalize from 'lodash/capitalize';
import { unstable_composeClasses as composeClasses } from '@mui/material';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';
import Paper, { PaperProps as MuiPaperProps } from '@mui/material/Paper';

const useUtilityClasses = (ownerState: any) => {
  const { classes, selected } = ownerState;

  const slots = {
    root: ['root', selected && `selected${capitalize(selected)}`],
    content: ['content'],
  };

  const composedClasses = composeClasses(slots, getToolbarUtilityClass, classes);

  return {
    ...classes,
    ...composedClasses,
  };
};

const PaneRoot = styled(Paper, {
  name: 'O2Pane',
  slot: 'Root',
  shouldForwardProp: (prop) => sysShouldForwardProp(prop) && prop !== 'ownerState',
})<any>(({ theme, color, info, variant, onClick }) => ({
  ...(info && {
    borderLeftWidth: 8,
    borderLeftStyle: 'solid',
    borderLeftColor: theme.palette[info]?.main ?? theme.palette.background[info],
  }),
  ...(color && {
    backgroundColor:
      color === 'transparent'
        ? 'transparent'
        : variant === 'outlined' && color !== 'internal'
        ? theme.palette.common.white
        : theme.palette.background[color],
  }),
  ...(onClick && {
    cursor: 'pointer',
  }),
}));

const PaneContent = styled(Box, {
  name: 'O2Pane',
  slot: 'Content',
})(() => ({
  position: 'relative',
}));

export interface PaneProps extends MuiPaperProps, PaddingProps, MarginProps {
  /**
   * Background color of the pane.
   */
  color?: 'primary' | 'success' | 'info' | 'warning' | 'error' | 'internal' | 'internalDark' | 'transparent';
  /**
   * Colored left border for info-panes.
   */
  info?: 'primary' | 'success' | 'info' | 'warning' | 'error' | 'internal';
  /**
   * Defaults to 'elevation'.
   */
  variant?: MuiPaperProps['variant'];

  /**
   * Blue border?
   */
  selected?: boolean;
}

/**
 * It's basically a Paper component with some extras like pre-defined colors or info-border (whatever that is).
 *
 * @deprecated Use `@o2/ui/Paper` instead.
 */
const Pane: FC<PaneProps> = (props) => {
  const { onClick, selected } = props;
  const paddings = pick(props, padding.filterProps);
  const shouldRenderPaddingWrapper = Object.keys(paddings).length > 0;
  let other = props;

  const ownerState = {
    ...props,
    onClick,
    selected,
  };

  const classes = useUtilityClasses(ownerState);

  if (shouldRenderPaddingWrapper) {
    other = omit(props, padding.filterProps);
  }

  return (
    <PaneRoot ownerState={ownerState} className={clsx(classes.root)} {...other}>
      {shouldRenderPaddingWrapper ? <PaneContent {...paddings}>{props.children}</PaneContent> : props.children}
    </PaneRoot>
  );
};

export default Pane;
